import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  FileProgress, Field, JiraLink, KEY_STATUS_LABEL, KeyStatusBadge, Kv, NewBadge, PageHeader, Pager, PersonPicker, PresenceBadge,
  RepoStatusBadge, RiskBadge, SecretsTabs, errMsg, fmtDate, fmtDateTime, invalidateFilters, useSecretFilters,
  useSecretsAccess, ReasonFields, FP_REASON_LABEL,
} from './common';
import { KeyAnalysisPanel } from './KeyAnalysis';

function EditCard({ k, onSaved }) {
  const [status, setStatus] = useState(k.status);
  const [revoker, setRevoker] = useState({ email: k.revoker_email || '', name: k.revoker_name || '' });
  const [jira, setJira] = useState(k.jira_key || '');
  const [notes, setNotes] = useState(k.notes || '');
  const [note, setNote] = useState('');
  const [fp, setFp] = useState({ reason: k.fp_reason || '', detail: k.fp_reason_detail || '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    setStatus(k.status);
    setFp({ reason: k.fp_reason || '', detail: k.fp_reason_detail || '' });
    setRevoker({ email: k.revoker_email || '', name: k.revoker_name || '' });
    setJira(k.jira_key || '');
    setNotes(k.notes || '');
  }, [k]);

  const save = () => {
    const body = { revoker_email: revoker.email, revoker_name: revoker.name, jira_key: jira, notes };
    if (note.trim()) body.note = note.trim();
    if (status === 'false_positive') {
      if (!fp.reason) { setMsg('Pick a reason for the false positive'); return; }
      body.fp_reason = fp.reason; body.fp_reason_detail = fp.detail;
      if (status === k.status && (fp.reason !== k.fp_reason || fp.detail !== (k.fp_reason_detail || ''))) body.status = status;
    }
    if (status !== k.status) {
      body.status = status;
      if (status === 'revoked' && k.status !== 'eligible') {
        if (!window.confirm('Not every repo has marked this key ready to revoke. Revoke it anyway?')) return;
        body.force = true;
      }
    }
    setBusy(true); setMsg('');
    axios.patch(`/secrets/keys/${k.id}`, body)
      .then((r) => { setMsg(r.data.changed.length ? 'Saved' : 'Nothing changed'); setNote(''); invalidateFilters(); onSaved(); })
      .catch((e) => setMsg(errMsg(e))).finally(() => setBusy(false));
  };

  const reviewNow = (decision) => {
    setBusy(true); setMsg('');
    axios.patch(`/secrets/keys/${k.id}`, { fp_review: decision, note: note.trim() || undefined })
      .then(() => { setMsg(decision === 'confirm' ? 'Confirmed' : 'Re-opened'); invalidateFilters(); onSaved(); })
      .catch((e) => setMsg(errMsg(e))).finally(() => setBusy(false));
  };

  const statusOptions = ['open', 'revoked', 'false_positive', 'wont_fix'];
  if (k.status === 'eligible') statusOptions.unshift('eligible');

  return (
    <div className="card">
      <h3 style={{ fontSize: 15, marginBottom: 14 }}>Rotation</h3>
      <Field label="Key status">
        <select className="form-input" value={status} onChange={(e) => setStatus(e.target.value)}>
          {statusOptions.map((s) => <option key={s} value={s}>{KEY_STATUS_LABEL[s]}</option>)}
        </select>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Eligible is set automatically once every repo marks the key ready to revoke.
        </div>
      </Field>
      {status === 'false_positive' && <ReasonFields reason={fp.reason} detail={fp.detail} onChange={setFp} />}
      {k.status === 'false_positive' && k.fp_marked_by === 'auto' && (
        <div className="card" style={{ padding: 12, marginBottom: 12, borderColor: k.fp_reviewed_at ? 'var(--border)' : '#fbbf24' }}>
          <div style={{ fontSize: 12, marginBottom: 8 }}>
            Marked automatically by <Link to="/secrets/rules">rule #{k.fp_rule_id}</Link>
            {k.fp_reviewed_at ? ` · reviewed by ${k.fp_reviewed_by}` : ' · awaiting review'}
          </div>
          {!k.fp_reviewed_at && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary" disabled={busy} onClick={() => reviewNow('confirm')}>Confirm false positive</button>
              <button className="btn btn-ghost" disabled={busy} onClick={() => reviewNow('reject')}>Not a false positive</button>
            </div>
          )}
        </div>
      )}
      <Field label="Revoker">
        <PersonPicker email={revoker.email} name={revoker.name} onChange={setRevoker} />
      </Field>
      <Field label="Jira card (key level)">
        <input className="form-input" value={jira} placeholder="SEC-123 or a Jira link" onChange={(e) => setJira(e.target.value)} />
      </Field>
      <Field label="Notes">
        <textarea className="form-input" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      <Field label="Reason for this change (audit log)">
        <input className="form-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" />
      </Field>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="btn btn-primary" disabled={busy} onClick={save}>Save</button>
        {busy && <span className="spinner" />}
        {msg && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{msg}</span>}
      </div>
    </div>
  );
}

function RepoRow({ keyId, r, jiraBase, onChanged, canEdit }) {
  const [confirmedBy, setConfirmedBy] = useState('');
  const [busy, setBusy] = useState(false);
  const flip = () => {
    const st = r.status === 'ready_to_revoke' ? 'open' : 'ready_to_revoke';
    setBusy(true);
    // one sign-off per scope: the repo root, or a folder with its own owner
    axios.put(`/secrets/keys/${keyId}/scopes/${r.scope_id}`, { status: st, confirmed_by: confirmedBy })
      .then(() => { setConfirmedBy(''); onChanged(); })
      .catch((e) => window.alert(errMsg(e))).finally(() => setBusy(false));
  };
  return (
    <tr>
      <td>
        <Link to={`/secrets/repos/${r.repo_id}`}>{r.repo_slug}</Link>
        {r.folder_path && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', wordBreak: 'break-all' }} title="A folder with an owner of its own">
            folder {r.folder_path}
          </div>
        )}
        <div style={{ fontSize: 11, marginTop: 2 }}>
          <a href={r.bitbucket_url} target="_blank" rel="noreferrer">Bitbucket</a>
          {r.confluence_url && <> · <a href={r.confluence_url} target="_blank" rel="noreferrer">Removal plan</a></>}
        </div>
      </td>
      <td style={{ fontSize: 12 }}>
        {r.owner_name || '—'}
        <div style={{ color: 'var(--text-muted)' }}>{r.owner_status}</div>
      </td>
      <td style={{ fontSize: 12 }}>{r.team || <span style={{ color: 'var(--text-muted)' }}>Unknown</span>}</td>
      <td>
        <PresenceBadge present={r.present_in_repo} removedAt={r.removed_detected_at} reason={r.removed_reason} />
        <FileProgress total={r.files_total} left={r.present_in_repo ? r.files_left : 0} />
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          {r.head_location_count} in code · {r.location_count} total
        </div>
      </td>
      <td>
        <RepoStatusBadge status={r.status} />
        {r.status === 'ready_to_revoke' && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            {r.confirmed_by ? `by ${r.confirmed_by}, ` : ''}{fmtDate(r.marked_at)}
          </div>
        )}
      </td>
      <td>
        {canEdit && <div style={{ display: 'flex', gap: 6 }}>
          {r.status !== 'ready_to_revoke' && (
            <input className="form-input" style={{ width: 150 }} placeholder={r.folder_path ? 'Confirmed by (folder owner)' : 'Confirmed by'}
              value={confirmedBy} onChange={(e) => setConfirmedBy(e.target.value)} />
          )}
          <button className={`btn ${r.status === 'ready_to_revoke' ? 'btn-ghost' : 'btn-secondary'}`} disabled={busy} onClick={flip}>
            {r.status === 'ready_to_revoke' ? 'Re-open' : 'Mark ready'}
          </button>
        </div>}
      </td>
      <td><JiraLink jiraKey={r.repo_jira_key} base={jiraBase} /></td>
    </tr>
  );
}

// Where a key was found. On the key page it covers every repo; pass
// fixedRepoId to lock it to one repo (the repo page embeds it per key row).
export function Locations({ keyId, repos = [], fixedRepoId, defaultScope = 'head', embedded = false }) {
  const [scope, setScope] = useState(defaultScope);
  const [pickedRepo, setRepoId] = useState('');
  const repoId = fixedRepoId || pickedRepo;
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ data: [], total: 0, page: 1, limit: 50 });
  useEffect(() => { setPage(1); }, [scope, repoId]);
  useEffect(() => {
    axios.get(`/secrets/keys/${keyId}/locations`, { params: { scope, repo_id: repoId || undefined, page, limit: 50 } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [keyId, scope, repoId, page]);
  return (
    <div className={embedded ? '' : 'card'} style={embedded ? { padding: '4px 0 8px' } : { marginTop: 16 }}>
      <div className="toolbar" style={embedded ? { marginBottom: 8 } : undefined}>
        <h3 style={{ fontSize: embedded ? 13 : 15 }}>{fixedRepoId ? 'Places in this repo' : 'Where it was found'}</h3>
        <span className="spacer" />
        {!fixedRepoId && (
          <select className="form-input" style={{ width: 200 }} value={repoId} onChange={(e) => setRepoId(e.target.value)}>
            <option value="">All repos</option>
            {repos.map((r) => <option key={r.repo_id} value={r.repo_id}>{r.repo_slug}</option>)}
          </select>
        )}
        <select className="form-input" style={{ width: 170 }} value={scope} onChange={(e) => setScope(e.target.value)}>
          <option value="head">Current code (at scan)</option>
          <option value="history">Git history</option>
          <option value="all">Everything</option>
        </select>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr>{!fixedRepoId && <th>Repo</th>}<th>File</th><th>Commit</th><th>Author</th><th>Rule</th><th>Now</th></tr></thead>
          <tbody>
            {data.data.map((l) => (
              <tr key={l.id}>
                {!fixedRepoId && <td style={{ fontSize: 12 }}>{l.repo_slug}</td>}
                <td style={{ fontSize: 12, maxWidth: 420, wordBreak: 'break-all' }}>
                  <a href={l.bitbucket_link} target="_blank" rel="noreferrer">{l.file_path}{l.line ? `:${l.line}` : ''}</a>
                  {l.source === 'scan' && <span className="badge badge-high" style={{ marginLeft: 6 }}>new</span>}
                  {l.folder_path && <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>owned by folder {l.folder_path}</div>}
                </td>
                <td style={{ fontSize: 12 }}><code>{l.commit_sha ? l.commit_sha.slice(0, 10) : 'HEAD'}</code>
                  {l.committed_at && <div style={{ color: 'var(--text-muted)' }}>{fmtDate(l.committed_at)}</div>}</td>
                <td style={{ fontSize: 12 }}>{l.author || '—'}<div style={{ color: 'var(--text-muted)' }}>{l.author_email}</div></td>
                <td style={{ fontSize: 12 }}>{l.rule}</td>
                <td style={{ fontSize: 12 }}>
                  {l.in_current_code ? (l.present_at_head === 0
                    ? <span style={{ color: '#86efac' }}>Gone</span>
                    : <span style={{ color: '#fbbf24' }}>Still there</span>) : <span style={{ color: 'var(--text-muted)' }}>History</span>}
                </td>
              </tr>
            ))}
            {data.data.length === 0 && <tr><td colSpan={fixedRepoId ? 5 : 6} style={{ color: 'var(--text-muted)', padding: 16 }}>Nothing here.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={data.page} limit={data.limit} total={data.total} onPage={setPage} />
    </div>
  );
}

export function EventList({ events }) {
  const label = {
    key_status: 'Key status', key_revoker: 'Revoker', key_revoker_name: 'Revoker name', key_jira: 'Key Jira card',
    key_notes: 'Notes', repo_notes: 'Repo notes', repo_signoff: 'Repo sign-off', repo_team: 'Team', repo_jira: 'Repo Jira card',
    presence_removed: 'Removed from code', presence_returned: 'Back in code', presence_new_repo: 'Found in a new repo',
    import: 'Import', access_grant: 'Shared tracker', access_revoke: 'Stopped sharing',
    bulk: 'Bulk action', key_search: 'Key search', access_denied: 'Access refused',
    fp_reason: 'False-positive reason', fp_review: 'Auto-mark review', auto_false_positive: 'Auto false positive',
    fp_rule_proposed: 'Rule proposed', fp_rule_approved: 'Rule approved', fp_rule_rejected: 'Rule rejected',
    fp_rule_suspended: 'Rule suspended', presence_new_key: 'New key found', repo_owner: 'Repo owner', repo_owner_details: 'Owner details',
    repo_archived: 'Repo archived', repo_unarchived: 'Repo out of archive',
    repo_missing: 'Repo not on Bitbucket', repo_found: 'Repo back on Bitbucket',
    key_analysis: 'Liveness analysis', key_probe: 'Liveness probe',
    folder_owner: 'Folder owner', scope_team: 'Folder team', rescope: 'Sign-off rows re-split by owner',
  };
  const bulkNote = (e) => {
    // "user note · {json}" → readable summary
    const i = (e.note || '').indexOf('{');
    let d = null;
    try { d = i >= 0 ? JSON.parse(e.note.slice(i)) : null; } catch { d = null; }
    const user = i > 0 ? e.note.slice(0, i).replace(/ · $/, '') : (i < 0 ? e.note : '');
    if (!d) return e.note;
    const parts = [];
    if (d.action === 'keys') {
      const f = d.fields || {};
      if (f.status) parts.push(`status → ${KEY_STATUS_LABEL[f.status] || f.status}`);
      if (f.revoker_email) parts.push(`revoker → ${f.revoker_name || f.revoker_email}`);
      if (f.jira_key) parts.push(`Jira → ${f.jira_key}`);
      if (d.force) parts.push('forced');
    } else if (d.action === 'repo_signoff' || d.action === 'gone_signoff') {
      parts.push(d.status === 'open' ? 're-opened' : 'marked ready to revoke');
      if (d.confirmed_by) parts.push(`confirmed by ${d.confirmed_by}`);
      if (d.only_absent) parts.push('only keys gone from code');
    } else if (d.action === 'owner_import') {
      parts.push('repo owners imported from the sheet');
      if (d.overwrite_manual) parts.push('owners set by hand overwritten');
    } else if (d.action === 'rescope') {
      return `${d.count} change(s) in ${d.repo}: sign-off rows re-split between the repo owner and ${d.rules} folder owner(s)${user ? ` · ${user}` : ''}`;
    }
    if (d.action === 'owner_import') return `${d.count} repo(s): ${parts.join(', ')}${user ? ` · ${user}` : ''}`;
    const scope = d.filters ? `all matching ${Object.entries(d.filters).map(([k, v]) => `${k}=${v}`).join(', ')}`
      : (d.selected === false ? 'whole repo' : 'selected rows');
    return `${d.count} item(s): ${parts.join(', ')} · ${scope}${user ? ` · ${user}` : ''}`;
  };
  if (!events.length) return <div style={{ color: 'var(--text-muted)' }}>No changes yet.</div>;
  return (
    <div className="table-wrap">
      <table>
        <thead><tr><th>When (local)</th><th>Who</th><th>What</th><th>Repo</th><th>Change</th><th>Note</th></tr></thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.id} style={e.action === 'bulk' ? { background: 'var(--bg-elevated)' } : undefined}>
              <td style={{ fontSize: 12, whiteSpace: 'nowrap' }} title={e.created_at}>{fmtDateTime(e.created_at)}</td>
              <td style={{ fontSize: 12 }} title={e.actor_ip ? `from ${e.actor_ip}` : ''}>{e.actor}</td>
              <td style={{ fontSize: 12 }}>{label[e.action] || e.action}
                {e.key_redacted && <div><Link to={`/secrets/keys/${e.key_id}`}><code>{e.key_redacted}</code></Link></div>}
                {e.batch_id && e.action !== 'bulk' && (
                  <div><Link to={`/secrets/activity?batch_id=${e.batch_id}`} style={{ fontSize: 11 }}>part of a batch</Link></div>
                )}
                {e.action === 'bulk' && (
                  <div><Link to={`/secrets/activity?batch_id=${e.batch_id}`} style={{ fontSize: 11 }}>show the {e.new_value} item(s)</Link></div>
                )}
              </td>
              <td style={{ fontSize: 12 }}>{e.repo_slug || ''}{e.folder_path ? <div style={{ color: 'var(--text-muted)' }}>folder {e.folder_path}</div> : null}</td>
              <td style={{ fontSize: 12 }}>
                {e.action === 'bulk' || e.action === 'import' ? '' :
                  (e.old_value || e.new_value ? `${e.old_value ?? '—'} → ${e.new_value ?? '—'}` : '')}
              </td>
              <td style={{ fontSize: 12, maxWidth: 420 }}>
                {e.action === 'bulk' ? bulkNote(e) : e.action === 'import' ? 'scan data loaded' : e.note}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SecretKeyDetail() {
  const { id } = useParams();
  const filters = useSecretFilters();
  const { can_edit: canEdit } = useSecretsAccess();
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => {
    axios.get(`/secrets/keys/${id}`).then((r) => setD(r.data)).catch((e) => setError(errMsg(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (error) return <div className="page-body"><div className="error-msg">{error}</div></div>;
  if (!d) return <div className="page-body"><span className="spinner" /></div>;
  const k = d.key;
  const ready = d.repos.filter((r) => r.status === 'ready_to_revoke').length;

  return (
    <div>
      <PageHeader title={<><code>{k.key_redacted}</code></>}>
        {k.secret_type} · {k.key_use || 'No key use recorded'} <em>(unreviewed guess)</em>
      </PageHeader>
      <div className="page-body">
        <SecretsTabs />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16 }}>
          <div className="card">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 8 }}>
              <Kv label="Status"><KeyStatusBadge status={k.status} /></Kv>
              <Kv label="Risk"><RiskBadge risk={k.risk_level} type={k.secret_type} /> {k.reachability && k.reachability !== 'n/a' && <small>{k.reachability}</small>}</Kv>
              <Kv label="In current code"><PresenceBadge present={k.present_in_any_repo} /></Kv>
              <Kv label="Found">{k.origin === 'scan'
                ? <><NewBadge since={k.first_seen_at} /> <span style={{ fontSize: 12 }}>first seen {fmtDateTime(k.first_seen_at)}</span></>
                : 'In the 23 Sep scan'}</Kv>
              <Kv label="Sign-offs">{ready} of {d.repos.length} ready{d.repos.some((r) => r.folder_path) ? ' (one per repo or owned folder)' : ''}</Kv>
              <Kv label="Found in">{k.occurrence_count} places</Kv>
              <Kv label="Gitleaks rules"><span style={{ fontSize: 12 }}>{(k.rules || '').split(',').join(', ')}</span></Kv>
              <Kv label="Confidence">{k.confidence || '—'}</Kv>
              {k.status === 'false_positive' && (
                <Kv label="False positive because">{FP_REASON_LABEL[k.fp_reason] || k.fp_reason || '—'}{k.fp_reason_detail ? ` — ${k.fp_reason_detail}` : ''}</Kv>
              )}
              <Kv label="Status set">{k.status_changed_at ? <>{fmtDate(k.status_changed_at)}{k.status_changed_by ? ` by ${k.status_changed_by}` : ''}</> : '—'}</Kv>
              <Kv label="Revoked">{k.revoked_at ? fmtDate(k.revoked_at) : '—'}</Kv>
              <Kv label="Jira"><JiraLink jiraKey={k.jira_key} base={filters?.jira_base_url} /></Kv>
              <Kv label="Revoker">{k.revoker_name || k.revoker_email || '—'}</Kv>
            </div>
            <Kv label="SHA-256"><code style={{ fontSize: 11, wordBreak: 'break-all' }}>{k.key_sha256}</code></Kv>
            {d.committers.length > 0 && (
              <Kv label={`Committers (${d.committers.length})`}>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{d.committers.slice(0, 20).join(', ')}{d.committers.length > 20 ? ' …' : ''}</span>
              </Kv>
            )}
          </div>
          {canEdit ? <EditCard k={k} onSaved={load} /> : (
            <div className="card">
              <h3 style={{ fontSize: 15, marginBottom: 14 }}>Notes</h3>
              <div style={{ whiteSpace: 'pre-wrap', color: k.notes ? 'var(--text-primary)' : 'var(--text-muted)' }}>{k.notes || 'None'}</div>
            </div>
          )}
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 15, marginBottom: 4 }}>Repos and folders holding this key</h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            One sign-off per repo, or per folder of a repo that has an owner of its own. The key becomes eligible once every row is ready.
          </p>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Repo / folder</th><th>Owner</th><th>Team</th><th>Code</th><th>Sign-off</th><th /><th>Repo Jira</th></tr></thead>
              <tbody>
                {d.repos.map((r) => <RepoRow key={r.key_repo_id} keyId={k.id} r={r} jiraBase={filters?.jira_base_url} onChanged={load} canEdit={canEdit} />)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 15, marginBottom: 4 }}>Liveness deep analysis</h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
            Reads how this key is used on the code box, then (after you confirm) probes the resource to see if the
            key is still accepted. The raw value and the raw probe output never leave the box.
          </p>
          <KeyAnalysisPanel keyId={k.id} onChanged={() => {}} />
        </div>

        <Locations keyId={k.id} repos={d.repos} />

        <div className="card" style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 15, marginBottom: 12 }}>History</h3>
          <EventList events={d.events} />
        </div>
      </div>
    </div>
  );
}
